import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue';
import type { Route } from 'vue-router';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router/composables';
import router from '@/router';
import CmsMenu from '@/modules/CMS/layout/CmsMenu.vue';
import SettingsTopBar from '@/modules/CMS/layout/SettingsTopBar.vue';
import { RouteNames } from '@/enums/routes/RouteNames';
import { CmsEntityTypes } from '@/enums/global/CmsEntityTypes';
import IntroScreenRepository from '@/repositories/IntroScreenRepository';
import IntroScreen from '@/models/IntroScreen';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { EventBus } from '@/helpers/EventBusHelper';
import { useUnsavedDataGuard } from '@/shared/composables/useUnsavedDataGuard';
import { useUserRights } from '@/shared/composables/useUserRights';


export default /*#__PURE__*/_defineComponent({
  __name: 'CmsContent',
  setup(__props) {

const backRoute = ref(RouteNames.settings);
const menuData = ref<any[]>([]);
const hasUnsavedData = ref(false);

useUnsavedDataGuard(hasUnsavedData);

const {
    canViewUsersTab,
    canSeeClientTypesItem,
    canSeeClientStatusItem,
    isSuperAdmin,
    canSeeCampaignsItem,
    canSeeCampaignTypesItem,
    canSeeLeadTypesItem,
    canSeeLeadOriginsItem,
    canSeeOrderPdfTemplatesItem,
    canSeeInfoButtonsItem,
    canSeeStatusesItem,
    canSeeTaxesItem,
    canSeeDocumentNamesItem,
    canSeeOfferConfigurationsItem,
    canSeeOrderStatusItem,
    canSeeAutomaticPriceManipulations,
    canSeeEmailTemplateProperties,
    isCurrentUserGroupRoltek,
    canSeeNextStepTypesItem,
    canSeePostCodeConnectionsItem,
    canSeeCurrenciesItem,
    canSeeUserGroupsItem,
} = useUserRights();

onBeforeRouteLeave(async (to, _from, next) => {
    if (to.name === RouteNames.contentSubcategory) {
        backRoute.value = RouteNames.cmsContent;
        createMenu(to);
        next();
    } else if (to.name === RouteNames.cmsContent) {
        backRoute.value = RouteNames.settings;
        createMenu(to);
        next();
    } else {
        next();
    }
});

onBeforeRouteUpdate((to, _from, next) => {
    createMenu(to);
    next();
});

function createMenu(route: Route) {
    if (route.params.subCategory === undefined) {
        menuData.value = [
            {
                icon: 'user',
                text: 'Client Content',
                canSee: canViewUsersTab.value,
                redirect: goToClientContent,
            },
            {
                icon: 'interaction',
                text: 'Inquiry content',
                canSee: true,
                redirect: goToInquiryContent,
            },
            {
                icon: 'hdd',
                text: 'Project content',
                canSee: true,
                redirect: goToProjectContent,
            },
            {
                icon: 'cluster',
                text: 'B2B settings content',
                canSee: true,
                redirect: goToB2BContent,
            },
            {
                icon: 'folder',
                text: 'Other content',
                canSee: true,
                redirect: goToOtherContent,
            },
        ];
    } else if (route.params.subCategory === 'client-content') {
        menuData.value = [
            {
                icon: 'snippets',
                text: 'Client role',
                canSee: canSeeClientTypesItem.value,
                redirect: goToClientTypes,
            },
            {
                icon: 'snippets',
                text: 'Client label',
                canSee: canSeeClientStatusItem.value,
                redirect: goToClientStatus,
            },
            {
                icon: 'euro',
                text: 'Vrsta plaćanja',
                canSee: true,
                redirect: goToPaymentTypes,
            },
            {
                icon: 'home',
                text: 'Address types',
                canSee: true,
                redirect: goToAddressTypes,
            },
            {
                icon: 'euro',
                text: 'price list changing',
                canSee: isSuperAdmin.value,
                redirect: goToPriceLists,
            },
            {
                icon: 'reconciliation',
                text: 'Accounting information',
                canSee: true,
                redirect: goToAccountingContent,
            },
            {
                icon: 'environment',
                text: 'Delivery regions',
                canSee: true,
                redirect: goToDeliveryRegions,
            },
        ];
    } else if (route.params.subCategory === 'accounting-content') {
        backRoute.value = RouteNames.contentSubcategory;

        menuData.value = [
            {
                type: 'header',
                text: 'BOOKING',
            },
            {
                text: 'Spl. knjižna skupina tržišča',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.GeneralBusinessPostingGroups),
            },
            {
                text: 'Knjižna skupina kupca',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.CustomerPostingGroups),
            },
            {
                text: 'Fin. sektor nerezidenta',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.NonResidentFinancialSectors),
            },
            {
                text: 'Kapitalska povezanost',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.CapitalConnections),
            },
            {
                text: 'Šifra finančnega sektorja',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.FinancialSectors),
            },
            {
                type: 'header',
                text: 'PAYMENTS',
            },
            {
                text: 'Šifra plačilnih pogojev',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.PaymentTerms),
            },
            {
                type: 'header',
                text: 'DELIVERY',
            },
            {
                text: 'Način dobave',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.ShipmentMethods),
            },
            {
                text: 'Šifra vrste transakcije',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.TransactionTypes),
            },
            {
                text: 'Šifra načina prevoza',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.TransportationTypes),
            },
            {
                type: 'header',
                text: 'CLIENT STATUS',
            },
            {
                text: 'Vrsta kontrole',
                canSee: true,
                redirect: () => goToAccountingEntity(CmsEntityTypes.ControlTypes),
            },
        ];
    } else if (route.params.subCategory === 'inquiry-content') {
        menuData.value = [
            {
                icon: 'calendar',
                text: 'Kampanje',
                canSee: canSeeCampaignsItem.value,
                redirect: goToCampaigns,
            },
            {
                icon: 'sliders',
                text: 'Tipovi kampanja',
                canSee: canSeeCampaignTypesItem.value,
                redirect: goToCampaignTypes,
            },
            {
                icon: 'file-ppt',
                text: 'Vrsta upita',
                canSee: canSeeLeadTypesItem.value,
                redirect: goToLeadTypes,
            },
            {
                icon: 'monitor',
                text: 'Izvor upita',
                canSee: canSeeLeadOriginsItem.value,
                redirect: goToLeadOrigins,
            },
        ];
    } else if (route.params.subCategory === 'project-content') {
        menuData.value = [
            {
                icon: 'form',
                text: 'Order for PDF templates',
                canSee: canSeeOrderPdfTemplatesItem.value,
                redirect: goToOrderPdfTemplatesIndex,
            },
            {
                icon: 'table',
                text: 'Polja formi',
                canSee: canSeeInfoButtonsItem.value,
                redirect: goToCheckListIndex,
            },
            {
                icon: 'key',
                text: 'Oznake',
                canSee: canSeeStatusesItem.value,
                redirect: goToLabels,
            },
            {
                icon: 'fork',
                text: 'Tijek rada',
                canSee: true,
                redirect: goToWorkflow,
            },
            {
                icon: 'profile',
                text: 'Porezi',
                canSee: canSeeTaxesItem.value,
                redirect: goToTaxes,
            },
            {
                icon: 'percentage',
                text: 'Detalji poreza na PDF-u',
                canSee: canSeeTaxesItem.value,
                redirect: goToTaxDetails,
            },
            {
                icon: 'mail',
                text: 'Vrste dokumenata',
                canSee: canSeeDocumentNamesItem.value,
                redirect: goToDocumentNames,
            },
            {
                icon: 'file-pdf',
                text: 'Opcije PDF-a',
                canSee: canSeeOfferConfigurationsItem.value,
                redirect: goToPdfOfferOptions,
            },
            {
                icon: 'rise',
                text: 'Status narudžbe',
                canSee: canSeeOrderStatusItem.value,
                redirect: goToOrderStatuses,
            },
            {
                icon: 'credit-card',
                text: 'Automatske promjene cijena',
                canSee: canSeeAutomaticPriceManipulations.value,
                redirect: goToAutomaticPriceManipulations,
            },
            {
                icon: 'message',
                text: 'Email predlošci',
                canSee: canSeeEmailTemplateProperties.value,
                redirect: goToEmailTemplateProperties,
            },
            {
                icon: 'flag',
                text: 'Flagged PDF values',
                canSee: true,
                redirect: goToFlaggedPdfValues,
            },
            {
                icon: 'alert',
                text: 'Automatic events for statuses',
                canSee: true,
                redirect: goToAutomaticEvents,
            },
            {
                icon: 'warning',
                text: 'Valid warranty warnings',
                canSee: true,
                redirect: goToWarrantyWarnings,
            },
        ];
    } else if (route.params.subCategory === 'b2b-content') {
        menuData.value = [
            {
                icon: 'control',
                text: 'Predlošci oznaka',
                canSee: isCurrentUserGroupRoltek.value,
                redirect: goToProjectLabelTemplates,
            },
            {
                icon: 'gateway',
                text: 'Predlošci tijekova rada',
                canSee: isCurrentUserGroupRoltek.value,
                redirect: goToWorkflowTemplate,
            },
        ];
    } else if (route.params.subCategory === 'other-content') {
        menuData.value = [
            {
                icon: 'double-right',
                text: 'Tipovi sljedećih koraka',
                canSee: canSeeNextStepTypesItem.value,
                redirect: goToNextStepTypes,
            },
            {
                icon: 'environment',
                text: 'Poštanski brojevi',
                canSee: canSeePostCodeConnectionsItem.value,
                redirect: goToPostCodeConnections,
            },
            {
                icon: 'dollar',
                text: 'Currencies',
                canSee: canSeeCurrenciesItem.value,
                redirect: goToCurrencies,
            },
            {
                icon: 'usergroup-add',
                text: 'Korisničke grupe',
                canSee: canSeeUserGroupsItem.value,
                redirect: goToUserGroups,
            },
            {
                icon: 'appstore',
                text: 'Početni zaslon',
                canSee: true,
                redirect: goToIntroScreenCms,
            },
            {
                icon: 'bank',
                text: 'Bankovni računi',
                canSee: true,
                redirect: goToBankAccounts,
            },
            {
                icon: 'camera',
                text: 'Login photos',
                canSee: true,
                redirect: goToLoginPhotos,
            },
            {
                icon: 'windows',
                text: 'Resources',
                canSee: true,
                redirect: goToResources,
            },
            {
                icon: 'calendar',
                text: 'Schedules',
                canSee: true,
                redirect: goToSchedules,
            },
            {
                icon: 'fund',
                text: 'Grupe proizvoda',
                canSee: true,
                redirect: goToProductGroups,
            },
            {
                icon: 'arrows-alt',
                text: 'Dimensions wizard',
                canSee: true,
                redirect: goToDimensionsWizard,
            },
            {
                icon: 'layout',
                text: 'Dashboards',
                canSee: true,
                redirect: goToDashboards,
            },
            {
                icon: 'pie-chart',
                text: 'Statistics',
                canSee: true,
                redirect: goToStatistics,
            },
            {
                icon: 'mail',
                text: 'News',
                canSee: true,
                redirect: goToNews,
            },
            {
                icon: 'carry-out',
                text: 'Holidays',
                canSee: true,
                redirect: goToHolidayCalendar,
            },
        ];
    }
}

function goToClientContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: {
            subCategory: 'client-content',
        },
    });
}
function goToInquiryContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: {
            subCategory: 'inquiry-content',
        },
    });
}
function goToProjectContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: {
            subCategory: 'project-content',
        },
    });
}
function goToB2BContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: {
            subCategory: 'b2b-content',
        },
    });
}

function goToOtherContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: {
            subCategory: 'other-content',
        },
    });
}

function goToCheckListIndex() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.ChecklistFields,
        },
    });
}

function goToOrderPdfTemplatesIndex() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.PdfOrderTemplatesOptions,
        },
    });
}

function goToLabels() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.Labels,
        },
    });
}

function goToProjectLabelTemplates() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.ProjectLabelTemplates,
        },
    });
}

function goToWorkflow() {
    router.push({
        name: RouteNames.workflow,
    });
}

function goToWorkflowTemplate() {
    router.push({
        name: RouteNames.workflowTemplate,
    });
}

function goToCampaignTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.CampaignTypes,
        },
    });
}

function goToCampaigns() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.Campaigns,
        },
    });
}

function goToNextStepTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.NextStepTypes,
        },
    });
}

function goToClientTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.ClientTypes,
        },
    });
}

function goToClientStatus() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.ClientStatus,
        },
    });
}

function goToTaxes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.Taxes,
        },
    });
}

function goToTaxDetails() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.TaxDetails,
        },
    });
}

function goToDocumentNames() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.DocumentNames,
        },
    });
}

function goToPdfOfferOptions() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.PdfOfferOptions,
        },
    });
}

function goToUserGroups() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.UserGroups,
        },
    });
}

function goToLeadTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.LeadTypes,
        },
    });
}

function goToBankAccounts() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.BankAccount,
        },
    });
}

function goToLoginPhotos() {
    router.push({
        name: RouteNames.loginImages,
    });
}

function goToLeadOrigins() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.LeadOrigins,
        },
    });
}

function goToCurrencies() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.Currency,
        },
    });
}

function goToOrderStatuses() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.OrderStatus,
        },
    });
}

function goToPaymentTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.PaymentTypes,
        },
    });
}

function goToAccountingContent() {
    router.push({
        name: RouteNames.contentSubcategory,
        params: { subCategory: 'accounting-content' },
    });
}

function goToAccountingEntity(entityName: string) {
    router.push({
        name: RouteNames.cmsShow,
        params: { entityName: entityName },
    });
}

function goToPriceLists() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.PriceLists,
        },
    });
}

function goToAddressTypes() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.AddressTypes,
        },
    });
}

function goToDeliveryRegions() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.DeliveryRegions,
        },
    });
}

function goToAutomaticPriceManipulations() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.AutomaticPriceManipulations,
        },
    });
}

function goToPostCodeConnections() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.PostCodeConnections,
        },
    });
}

function goToEmailTemplateProperties() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.EmailTemplateProperties,
        },
    });
}

function goToProductGroups() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.ProductGroups,
        },
    });
}

async function goToDashboards() {
    router.push({
        name: RouteNames.dashboards,
    });
}
function goToFlaggedPdfValues() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.FlaggedPdfValues,
        },
    });
}

async function goToStatistics() {
    router.push({
        name: RouteNames.cmsStatistics,
    });
}
function goToResources() {
    router.push({
        name: RouteNames.schedulesResources,
        params: {
            dynamic: 'CalendarResources',
        },
    });
}
function goToSchedules() {
    router.push({
        name: RouteNames.schedulesResources,
        params: {
            dynamic: 'CalendarSchedules',
        },
    });
}
function goToDimensionsWizard() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.DimensionsWizard,
        },
    });
}
async function goToIntroScreenCms() {
    await IntroScreen.getAll();
    const introScreen = IntroScreenRepository.getFirst();

    if (introScreen == null) {
        return;
    }

    router.push({
        name: RouteNames.cmsIndex,
        params: {
            entityName: CmsEntityTypes.IntroScreenItem,
            entityId: introScreen.id,
        },
    });
}
async function goToNews() {
    router.push({
        name: RouteNames.cmsNews,
    });
}

function goToHolidayCalendar() {
    router.push({
        name: RouteNames.holidayCalendar,
    });
}

function goToProductDetails() {
    EventBus.$emit(EventBusEvents.deselectSelectedProductSystem);
    router.push({
        name: RouteNames.cmsIndex,
        params: {
            entityName: CmsEntityTypes.ProductDetails,
            entityId: 'start',
        },
    });
}

function goToAutomaticEvents() {
    router.push({
        name: RouteNames.automaticEvents,
    });
}

function goToWarrantyWarnings() {
    router.push({
        name: RouteNames.cmsShow,
        params: {
            entityName: CmsEntityTypes.WarrantyWarnings,
        },
    });
}

createMenu(router.currentRoute);

return { __sfc: true,backRoute, menuData, hasUnsavedData, canViewUsersTab, canSeeClientTypesItem, canSeeClientStatusItem, isSuperAdmin, canSeeCampaignsItem, canSeeCampaignTypesItem, canSeeLeadTypesItem, canSeeLeadOriginsItem, canSeeOrderPdfTemplatesItem, canSeeInfoButtonsItem, canSeeStatusesItem, canSeeTaxesItem, canSeeDocumentNamesItem, canSeeOfferConfigurationsItem, canSeeOrderStatusItem, canSeeAutomaticPriceManipulations, canSeeEmailTemplateProperties, isCurrentUserGroupRoltek, canSeeNextStepTypesItem, canSeePostCodeConnectionsItem, canSeeCurrenciesItem, canSeeUserGroupsItem, createMenu, goToClientContent, goToInquiryContent, goToProjectContent, goToB2BContent, goToOtherContent, goToCheckListIndex, goToOrderPdfTemplatesIndex, goToLabels, goToProjectLabelTemplates, goToWorkflow, goToWorkflowTemplate, goToCampaignTypes, goToCampaigns, goToNextStepTypes, goToClientTypes, goToClientStatus, goToTaxes, goToTaxDetails, goToDocumentNames, goToPdfOfferOptions, goToUserGroups, goToLeadTypes, goToBankAccounts, goToLoginPhotos, goToLeadOrigins, goToCurrencies, goToOrderStatuses, goToPaymentTypes, goToAccountingContent, goToAccountingEntity, goToPriceLists, goToAddressTypes, goToDeliveryRegions, goToAutomaticPriceManipulations, goToPostCodeConnections, goToEmailTemplateProperties, goToProductGroups, goToDashboards, goToFlaggedPdfValues, goToStatistics, goToResources, goToSchedules, goToDimensionsWizard, goToIntroScreenCms, goToNews, goToHolidayCalendar, goToProductDetails, goToAutomaticEvents, goToWarrantyWarnings, CmsMenu, SettingsTopBar }
}

})